// ===========================================
// BUSTED! BLACKJACK - FORMAT UTILITIES
// ===========================================
// Centralized formatting functions for consistent display
// ===========================================

/**
 * Format a currency amount for display.
 * Shows cents only when the amount has a fractional component.
 * Uses 'en-US' locale to ensure comma separators for thousands.
 *
 * @param amount - The amount to format (can be fractional, string or number)
 * @returns Formatted string (e.g., "102" or "102.50")
 *
 * @example
 * formatCurrency(100)       // "100"
 * formatCurrency(102.5)     // "102.50"
 * formatCurrency(1000)      // "1,000"
 * formatCurrency("1000.5")  // "1,000.50"
 */
export function formatCurrency(amount: number | string): string {
  // Convert to number if string (PostgreSQL DECIMAL returns strings)
  const num = typeof amount === 'string' ? parseFloat(amount) : amount;

  // Handle NaN or invalid input
  if (isNaN(num)) {
    return '0';
  }

  // Check if amount has fractional component
  const hasFraction = num % 1 !== 0;

  if (hasFraction) {
    // Show exactly 2 decimal places with comma separators
    return num.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  } else {
    // Whole number - no decimals, with comma separators
    return num.toLocaleString('en-US');
  }
}

/**
 * Format a balance amount for display.
 * Alias for formatCurrency for semantic clarity.
 */
export const formatBalance = formatCurrency;

/**
 * Format a chip/bet amount for display.
 * Alias for formatCurrency for semantic clarity.
 */
export const formatChips = formatCurrency;
